import './reviews-switcher.scss';

/**
 * Accessible tabs behavior for the reviews switcher.
 *
 * - Keeps legacy UI classes (`uk-active`) in sync for styling.
 * - Wires ARIA roles/states for tablist, tab, and tabpanel.
 * - Supports keyboard navigation (Arrow keys, Home, End).
 */
( function() {

	const ROOT = '.tm-reviews-switcher';
	const ACTIVE = 'uk-active';
	const RESIZE_DEBOUNCE_MS = 150;

	const debounce = ( fn, delay ) => {
		let t = null;
		return ( ...args ) => {
			window.clearTimeout( t );
			t = window.setTimeout( () => fn( ...args ), delay );
		};
	};

	const refreshActivePanelLayout = ( switcher ) => {
		const active = switcher.querySelector( ':scope > .' + ACTIVE );
		if ( ! active ) {
			return;
		}
		void active.offsetHeight;
	};

	const setIndex = ( pairs, index, switcher, focusTab = false ) => {
		if ( index < 0 || index >= pairs.length ) {
			return;
		}

		pairs.forEach( ( pair, i ) => {
			const isActive = i === index;

			pair.tabLi.classList.toggle( ACTIVE, isActive );
			pair.paneLi.classList.toggle( ACTIVE, isActive );

			pair.tabLink.setAttribute( 'aria-selected', isActive ? 'true' : 'false' );
			pair.tabLink.setAttribute( 'tabindex', isActive ? '0' : '-1' );

			if ( isActive ) {
				pair.paneLi.removeAttribute( 'hidden' );
				pair.paneLi.setAttribute( 'aria-hidden', 'false' );
			} else {
				pair.paneLi.setAttribute( 'hidden', '' );
				pair.paneLi.setAttribute( 'aria-hidden', 'true' );
			}
		} );

		if ( focusTab ) {
			pairs[ index ].tabLink.focus();
		}

		refreshActivePanelLayout( switcher );
	};

	const panelFromLink = ( link, panes ) => {
		const href = link.getAttribute( 'href' ) || '';
		if ( href.startsWith( '#' ) && href.length > 1 ) {
			const panelId = href.slice( 1 );
			const pane = panes.find( ( li ) => li.id === panelId );
			if ( pane ) {
				return pane;
			}
		}

		return null;
	};

	const initBlock = ( root ) => {
		if ( ! root || root.dataset.reviewsSwitcherInit === '1' ) {
			return;
		}

		const subnav = root.querySelector( '.uk-subnav' );
		if ( ! subnav ) {
			return;
		}

		const switcher = root.querySelector( '.uk-switcher' ) || subnav.nextElementSibling;
		if ( ! switcher || ! switcher.classList.contains( 'uk-switcher' ) ) {
			return;
		}

		const paneEls = Array.from( switcher.querySelectorAll( ':scope > li' ) );
		const tabLis  = Array.from( subnav.querySelectorAll( ':scope > li' ) );
		if ( paneEls.length === 0 || tabLis.length === 0 ) {
			return;
		}

		const pairs = tabLis
			.map( ( tabLi, i ) => {
				const tabLink = tabLi.querySelector( 'a, button' );
				if ( ! tabLink ) {
					return null;
				}

				const paneFromHref = panelFromLink( tabLink, paneEls );
				const paneLi = paneFromHref || paneEls[ i ];
				if ( ! paneLi ) {
					return null;
				}

				return { tabLi, tabLink, paneLi };
			} )
			.filter( Boolean );

		if ( pairs.length === 0 ) {
			return;
		}

		subnav.setAttribute( 'role', 'tablist' );

		pairs.forEach( ( pair, i ) => {
			if ( ! pair.tabLink.id ) {
				pair.tabLink.id = `${ root.id || 'reviews-switcher' }-tab-${ i + 1 }`;
			}

			if ( ! pair.paneLi.id ) {
				pair.paneLi.id = `${ root.id || 'reviews-switcher' }-panel-${ i + 1 }`;
			}

			pair.tabLink.setAttribute( 'role', 'tab' );
			pair.tabLink.setAttribute( 'aria-controls', pair.paneLi.id );

			pair.paneLi.setAttribute( 'role', 'tabpanel' );
			pair.paneLi.setAttribute( 'aria-labelledby', pair.tabLink.id );
			pair.paneLi.setAttribute( 'tabindex', '0' );
		} );

		let activeIndex = pairs.findIndex( ( pair ) => pair.tabLi.classList.contains( ACTIVE ) || pair.paneLi.classList.contains( ACTIVE ) );
		if ( activeIndex < 0 ) {
			activeIndex = 0;
		}

		root.dataset.reviewsSwitcherInit = '1';
		setIndex( pairs, activeIndex, switcher );

		subnav.addEventListener( 'click', ( event ) => {
			const link = event.target.closest( '[role="tab"]' );
			if ( ! link || ! subnav.contains( link ) ) {
				return;
			}
			event.preventDefault();

			const index = pairs.findIndex( ( pair ) => pair.tabLink === link );
			if ( index === -1 ) {
				return;
			}

			setIndex( pairs, index, switcher );
		} );

		subnav.addEventListener( 'keydown', ( event ) => {
			const current = event.target.closest( '[role="tab"]' );
			if ( ! current || ! subnav.contains( current ) ) {
				return;
			}

			const currentIndex = pairs.findIndex( ( pair ) => pair.tabLink === current );
			if ( currentIndex < 0 ) {
				return;
			}

			let nextIndex = null;
			switch ( event.key ) {
				case 'ArrowRight':
				case 'ArrowDown':
					nextIndex = ( currentIndex + 1 ) % pairs.length;
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					nextIndex = ( currentIndex - 1 + pairs.length ) % pairs.length;
					break;
				case 'Home':
					nextIndex = 0;
					break;
				case 'End':
					nextIndex = pairs.length - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			setIndex( pairs, nextIndex, switcher, true );
		} );

		const onResize = debounce( () => {
			refreshActivePanelLayout( switcher );
		}, RESIZE_DEBOUNCE_MS );

		window.addEventListener( 'resize', onResize, { passive: true } );
		window.addEventListener( 'orientationchange', onResize, { passive: true } );
	};

	const init = () => {
		document.querySelectorAll( ROOT ).forEach( initBlock );
	};

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', init );
	} else {
		init();
	}

} )();
